import { useState, useEffect } from "react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Eye, Users, CalendarCheck, Sparkles, Star, MousePointerClick } from "lucide-react";
import api from "@/lib/api";

const RANGES = [7, 30, 90];

function Kpi({ icon: Icon, label, value, hint }) {
  return (
    <div className="glass rounded-xl p-5" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between mb-2"><p className="text-[#2A1F14]/50 text-xs uppercase tracking-wider">{label}</p><Icon size={15} className="text-gold" /></div>
      <p className="font-display text-4xl text-gold">{value}</p>
      {hint && <p className="text-[#2A1F14]/45 text-xs mt-1">{hint}</p>}
    </div>
  );
}

function Bars({ rows, labelKey, valueKey, testid, suffix = "" }) {
  const max = Math.max(1, ...rows.map((r) => r[valueKey]));
  return (
    <div className="space-y-3" data-testid={testid}>
      {rows.length === 0 && <p className="text-[#2A1F14]/40 text-sm">Nessun dato ancora.</p>}
      {rows.map((r, i) => (
        <div key={i}>
          <div className="flex justify-between text-sm mb-1"><span className="text-[#2A1F14]/80 truncate pr-4">{r[labelKey]}</span><span className="text-[#2A1F14]/55 shrink-0">{r[valueKey]}{suffix}</span></div>
          <div className="h-1.5 rounded-full bg-[#2A1F14]/8 overflow-hidden"><div className="h-full bg-gold rounded-full" style={{ width: `${(r[valueKey] / max) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/analytics", { params: { days } }).then((r) => setData(r.data)).catch(() => {}); }, [days]);
  if (!data) return <p className="text-[#2A1F14]/40">Caricamento…</p>;
  const t = data.totals;
  const pct = (a, b) => (b ? `${Math.round((a / b) * 100)}%` : "—");
  const series = data.daily.map((d) => ({ ...d, label: d.day.slice(5).replace("-", "/") }));

  return (
    <div data-testid="admin-analytics">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="text-[#2A1F14] font-medium">Analytics first-party</p>
          <p className="text-[#2A1F14]/45 text-xs">Nessun cookie · nessun servizio esterno · IP mai salvato (visitatori conteggiati con impronta anonima giornaliera)</p>
        </div>
        <div className="flex gap-1 bg-[#2A1F14]/5 rounded-full p-1 border border-[#2A1F14]/10">
          {RANGES.map((r) => (
            <button key={r} onClick={() => setDays(r)} data-testid={`analytics-range-${r}`} className={`px-4 py-1.5 rounded-full text-xs transition-colors ${days === r ? "bg-gold text-black" : "text-[#2A1F14]/60 hover:text-[#2A1F14]"}`}>{r} giorni</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-10">
        <Kpi icon={Eye} label="Visualizzazioni" value={t.pageviews} />
        <Kpi icon={Users} label="Visitatori" value={t.visitors} hint="unici stimati" />
        <Kpi icon={MousePointerClick} label="Click Prenota" value={t.book_clicks} hint={`${pct(t.book_clicks, t.pageviews)} delle viste`} />
        <Kpi icon={CalendarCheck} label="Richieste inviate" value={t.book_submits} hint={`conversione ${pct(t.book_submits, t.book_clicks)}`} />
        <Kpi icon={Sparkles} label="ARIES" value={t.aries_opens} hint={`${t.aries_messages} messaggi · ${t.aries_recommendations} consigli`} />
        <Kpi icon={Star} label="Temi natali" value={t.natal_charts} hint={`${t.natal_pdfs || 0} PDF scaricati`} />
      </div>

      <div className="glass rounded-2xl p-6 mb-10" data-testid="analytics-chart">
        <p className="text-[#2A1F14]/50 text-xs uppercase tracking-wider mb-4">Andamento giornaliero</p>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="pv" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#B9863F" stopOpacity={0.35} /><stop offset="100%" stopColor="#B9863F" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(42,31,20,0.08)" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "rgba(42,31,20,0.5)" }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11, fill: "rgba(42,31,20,0.5)" }} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#FAF3E4", border: "1px solid rgba(185,134,63,0.35)", borderRadius: 12, fontSize: 12 }} />
              <Area type="monotone" dataKey="pageviews" name="Visualizzazioni" stroke="#B9863F" fill="url(#pv)" strokeWidth={2} />
              <Area type="monotone" dataKey="visitors" name="Visitatori" stroke="#2A1F14" strokeOpacity={0.5} fill="none" strokeWidth={1.5} strokeDasharray="4 4" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="glass rounded-2xl p-6">
          <p className="text-[#2A1F14]/50 text-xs uppercase tracking-wider mb-5">Pagine più viste</p>
          <Bars rows={data.top_pages} labelKey="path" valueKey="views" testid="top-pages" />
        </div>
        <div className="glass rounded-2xl p-6">
          <p className="text-[#2A1F14]/50 text-xs uppercase tracking-wider mb-5">Servizi più richiesti (click Prenota)</p>
          <Bars rows={data.top_services} labelKey="service" valueKey="clicks" testid="top-services" />
        </div>
        <div className="glass rounded-2xl p-6">
          <p className="text-[#2A1F14]/50 text-xs uppercase tracking-wider mb-5">Dispositivi</p>
          <Bars rows={data.devices} labelKey="device" valueKey="views" testid="devices" />
        </div>
      </div>
    </div>
  );
}
